import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Collapse,
  Alert,
  AlertTitle,
  TextField,
  Box,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Checkbox,
} from '@mui/material';
import {
  getErrorMessage,
  getLabelForCheckbox,
  resetAllErrorInfo,
} from '../../services/DataHelperFunctions';
import { EActionType, EFormFieldType } from '../../data/Constants';
import { CheckboxGroup } from './CheckboxGroup';
import { t } from 'i18next';

import type {
  IFormRule,
  IGenericSimpleFormProps,
  ICheckboxChangeField,
  IFormField,
  ICheckboxFieldProps,
} from '../../../types';

/**
 * Sets the disabled state for form fields based on their disable conditions
 *
 * @param {any[]} formFields - Array of form field configuration objects
 * @param {function} getValues - Function to retrieve current form values by field name
 * @param {function} setDisabledFields - State setter function to update disabled fields record
 *
 * @description
 * This function evaluates each form field's disable conditions and determines whether
 * the field should be disabled. It checks for two types of disable conditions:
 * - 'isActive': Field is disabled when the form's isActive value is false
 * - 'edit': Field is always disabled (typically used in edit mode)
 *
 * The function creates a record mapping field names to their disabled state and
 * updates the component's disabled fields state.
 *
 * @example
 * // Form field with disable conditions
 * const formFields = [
 *   {
 *     field: 'name',
 *     disableConditions: ['isActive']
 *   },
 *   {
 *     field: 'code',
 *     disableConditions: ['edit']
 *   }
 * ];
 *
 * // Usage in component
 * setFieldDisabled(formFields, getValues, setDisabledFields);
 */
const updateDisabledField = (
  formFields: any[],
  getValues: (field: string) => any,
  setDisabledFields: (fields: Record<string, boolean>) => void
) => {
  const disabledFields: Record<string, boolean> = {};
  formFields.forEach((f) => {
    disabledFields[f.field] = false;
    if (
      (f.disableConditions?.indexOf('isActive') >= 0 && !getValues('isActive')) ||
      f.disableConditions?.indexOf('edit') >= 0
    ) {
      disabledFields[f.field] = true;
    }
  });
  setDisabledFields(disabledFields);
};

/**
 * Renders a checkbox field
 * 
 * @param {ICheckboxFieldProps} props - Component props
 * @param {IFormField} props.formField - The form field configuration containing checkbox values and settings
 * @param {Control} props.control - React Hook Form control object for managing form state
 * @param {function} props.normalizeRules - Function to normalize validation rules for react-hook-form
 * @param {function} props.checkFieldDisabled - Function to determine if a specific field should be disabled
 * @param {TFunction} props.t - The translation function from i18next for internationalization
 * @param {function} props.handleCheckboxChange - Event handler function for checkbox state changes
 * 
 * @returns {JSX.Element[]} An array of Controller components wrapping FormControlLabel with Checkbox inputs
 * 
 * @description
 * This component maps over the values in formField.values and creates individual checkbox inputs
 * for each value. Each checkbox is:
 * - Wrapped in a React Hook Form Controller for form state management
 * - Bound to validation rules through normalizeRules
 * - Labeled using internationalized labels via getLabelForCheckbox
 * - Conditionally disabled based on checkFieldDisabled logic
 * - Connected to change handling through handleCheckboxChange
 * 
 * The component uses the fieldConfig object which contains:
 * - name: The field identifier
 * - code: The code value for the checkbox
 * 
 * Each checkbox is rendered with:
 * - A unique key based on fieldConfig.name
 * - FormControlLabel for proper accessibility
 * - Field spreading (...field) for react-hook-form integration
 * - Custom onChange handler that wraps the original field onChange
 * 
 
 */
const RenderCheckbox = ({
  formField,
  control,
  normalizeRules,
  checkFieldDisabled,
  handleCheckboxChange,
  manipulationType,
  oldFormData,
}: ICheckboxFieldProps) => {
  return formField.values
    ?.filter((fieldConfig: { name: string; code: string }) => {
      // Exclude 'isActive' checkbox if manipulation type = create and in case of edit if isActive=true
      if (
        (fieldConfig.name === 'isActive' &&
          manipulationType === EActionType.Edit &&
          oldFormData.isActive) ||
        (fieldConfig.name === 'isActive' && manipulationType === EActionType.Create)
      ) {
        return false;
      }
      return true;
    })
    .map((fieldConfig: { name: string; code: string }) => (
      <Controller
        key={fieldConfig.name}
        name={fieldConfig.name}
        control={control}
        rules={normalizeRules(formField.rules)}
        render={({ field }) => (
          <FormControlLabel
            label={getLabelForCheckbox(formField.labelFormat, fieldConfig.code, fieldConfig.name)}
            control={
              <Checkbox
                {...field}
                checked={field.value}
                onChange={(e) => handleCheckboxChange(e, field, fieldConfig)}
                disabled={checkFieldDisabled(formField.field, fieldConfig.code)}
              />
            }
          />
        )}
      />
    ));
};

export const GenericSimpleForm: React.FC<IGenericSimpleFormProps> = (props) => {
  const { formFields, initialData, manipulationType, setDisplayAlertMessage, uniqueKey } = props;
  const oldFormData = JSON.parse(JSON.stringify(initialData));
  const [disabledFields, setDisabledFields] = useState<Record<string, boolean>>({});
  const [shouldValidate, setShouldValidate] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    trigger,
    formState: { errors, isValid, isDirty },
  } = useForm<Record<string, any>>({
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    defaultValues: initialData,
  });

  useEffect(() => {
    reset(initialData);

    if (manipulationType === EActionType.Edit) {
      updateDisabledField(formFields, getValues, setDisabledFields);
      if (initialData[uniqueKey]?.length > 0) {
        setShouldValidate(true);
      }
    }

    // Signal that validation should run
  }, [initialData]);

  // Trigger validation after reset completes
  useEffect(() => {
    if (shouldValidate) {
      trigger();
      setShouldValidate(false);
    }
  }, [shouldValidate, trigger]);

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: ICheckboxChangeField,
    fieldData: { name: string; code: string }
  ) => {
    const isChecked = e.target.checked;

    field.onChange(isChecked);

    if (fieldData.name === 'isActive') {
      updateDisabledField(formFields, getValues, setDisabledFields);
    }
  };

  // Helper to ensure all rule messages are strings (not undefined)

  const normalizeRules = (rules?: IFormRule): IFormRule | undefined => {
    if (!rules) return undefined;
    return rules;
  };

  const checkFieldDisabled = (formField: string, fieldName: string) => {
    return disabledFields[formField] && fieldName !== 'isActive';
  };
  return (
    <form onSubmit={handleSubmit(props.onSubmit)}>
      {formFields.map((formField: IFormField) => {
        const label = t(`${'labels.' + formField.field}`);
        switch (formField.type) {
          case EFormFieldType.Text:
            return (
              <>
                <Controller
                  name={formField.field}
                  control={control}
                  rules={normalizeRules(formField.rules)}
                  render={({ field }) => (
                    <TextField
                      className="mt-1em"
                      {...field}
                      label={
                        <>
                          {label}
                          {formField.rules?.required && <span className="asterisk"> *</span>}
                        </>
                      }
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={disabledFields[formField.field]}
                      slotProps={{
                        htmlInput: { 'data-testid': 'role-name-input' },
                      }}
                    />
                  )}
                />

                <Collapse in={!!errors[formField.field]}>
                  <Alert className="errorMessage" severity="error">
                    <AlertTitle>
                      {getErrorMessage(errors, formField.field, t, formField.rules || {})}
                    </AlertTitle>
                  </Alert>
                </Collapse>
              </>
            );

          case EFormFieldType.RadioGroup:
            return !formField.values || formField.values.length === 0 ? (
              <p>{t('messages.noDataFound')}</p>
            ) : (
              <fieldset
                className="inputFieldset w-100 mt-1em"
                disabled={disabledFields[formField.field]}
              >
                <legend className="inputLegend" id={formField.field}>
                  {label} {formField.rules?.required?.value && <span className="asterisk">*</span>}
                </legend>
                <Controller
                  name={formField.field}
                  control={control}
                  rules={normalizeRules(formField.rules)}
                  render={({ field }) => (
                    <RadioGroup {...field}>
                      <Grid container>
                        {formField.values?.map((value) => {
                          return (
                            <Grid key={value.code} className="pt-0" size={4}>
                              <FormControlLabel
                                value={value.code || value.name}
                                key={value.code || value.name}
                                control={<Radio />}
                                label={value.name}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </RadioGroup>
                  )}
                />
              </fieldset>
            );

          case EFormFieldType.CheckboxGroup: {
            const legendLabel = formField.legend ? t(formField.legend) : label;
            return (
              <CheckboxGroup
                formField={formField}
                label={legendLabel}
                checkFieldDisabled={checkFieldDisabled}
                control={control}
                gridClass="checkbox-grid-3col"
              />
            );
          }

          case EFormFieldType.Checkbox:
            if (
              (formField.field == 'isActive' &&
                manipulationType === EActionType.Edit &&
                oldFormData.isActive) ||
              (formField.field == 'isActive' && manipulationType === EActionType.Create)
            ) {
              return null;
            }
            if (formField.showFormLegend) {
              return (
                <fieldset className="inputFieldset w-100 mt-1em">
                  <legend className="inputLegend" id="views">
                    {label} {formField.rules?.required && <span className="asterisk">*</span>}
                  </legend>
                  <div className="checkbox-grid">
                    <RenderCheckbox
                      formField={formField}
                      control={control}
                      normalizeRules={normalizeRules}
                      checkFieldDisabled={checkFieldDisabled}
                      handleCheckboxChange={handleCheckboxChange}
                      manipulationType={manipulationType}
                      oldFormData={oldFormData}
                    />
                  </div>
                </fieldset>
              );
            } else {
              return (
                <RenderCheckbox
                  formField={formField}
                  control={control}
                  normalizeRules={normalizeRules}
                  checkFieldDisabled={checkFieldDisabled}
                  handleCheckboxChange={handleCheckboxChange}
                  manipulationType={manipulationType}
                  oldFormData={oldFormData}
                />
              );
            }
          default:
            return null;
        }
      })}

      <Box className="formActionButtons">
        <Button
          type="submit"
          data-testid="role-button-submit"
          variant="contained"
          size="medium"
          className="text-capitalize"
          disabled={!isValid || !isDirty}
        >
          {manipulationType === EActionType.Create ? t('button.create') : t('button.save')}
        </Button>
        <Button
          size="medium"
          data-testid="role-button-cancel"
          className="text-capitalize"
          onClick={() => {
            reset();
            resetAllErrorInfo();
            if (setDisplayAlertMessage) {
              setDisplayAlertMessage(false);
            }
          }}
        >
          {t('button.cancel')}
        </Button>
      </Box>
    </form>
  );
};
